"use client"
import { useEffect, useState, useMemo, useCallback } from "react"
import { getDatabase, ref, onValue, get } from "firebase/database"
import { getStorage, ref as storageRef, getDownloadURL, listAll } from "firebase/storage"
import { format } from "date-fns"
import { ChevronDown, ChevronUp, ExternalLink } from "lucide-react"
import "./ClientInfoDashboard.css"


const ClientInfoDashboard = () => {
  const [clients, setClients] = useState([])
  const [selectedClientId, setSelectedClientId] = useState("")
  const [clientData, setClientData] = useState({})
  const [timePeriod, setTimePeriod] = useState("Last 1 Month")
  const [pendingItems, setPendingItems] = useState([])
  const [userDocuments, setUserDocuments] = useState({})
  const [mtcFiles, setMtcFiles] = useState([])
  const [statementFiles, setStatementFiles] = useState([])
  const [userCodes, setUserCodes] = useState([])
  const [orderHistory, setOrderHistory] = useState([])
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0)
  const [refunds, setRefunds] = useState([])
 const [activePOList, setActivePOList] = useState([]);
 
  // Toggle states for each section
  const [toggleStates, setToggleStates] = useState({
    personalDetails: true,
    siteBalances: false,
    refundRequests: false,
    queryHistory: false,
    quoteHistory: false,
    purchaseOrders: false,
    pendingActions: false,
    newCodes: false,
    newCodeRequests: false,
    liftingSummary: false,
  })

  const db = getDatabase()
  const storage = getStorage()

  const toggleSection = (section) => {
    setToggleStates((prev) => ({
      ...prev,
      [section]: !prev[section],
    }))
  }
  

  // Fetch clients
  useEffect(() => {
    const profileRef = ref(db, "profileDashboard")
    const unsub = onValue(
      profileRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const profiles = snapshot.val()
          const clientList = Object.entries(profiles).map(([uid, val]) => ({
            id: uid,
            name: val.company_name || "Unnamed",
          }))
          setClients(clientList)
        } else {
          setClients([])
        }
      },
      (err) => console.error("Error fetching profiles:", err),
    )
    return () => unsub()
  }, [db])

  // Fetch user documents
  const fetchUserDocuments = useCallback(
    async (userId) => {
      if (!userId) return
      try {
        const documentFields = ["gstCopy", "panCard", "workOrder", "purchaseOrder", "partnershipDeed", "cancelCheque"]
        const documents = {}
        await Promise.all(
          documentFields.map(async (field) => {
            const docRef = storageRef(storage, `userDocuments/${userId}/${field}`)
            try {
              const url = await getDownloadURL(docRef)
              documents[field] = url
            } catch (error) {
              documents[field] = null
            }
          }),
        )
        setUserDocuments(documents)
      } catch (err) {
        console.error(`Error fetching user documents: ${err.message}`)
        setUserDocuments({})
      }
    },
    [storage],
  )

  // Fetch MTC and statement files
  const fetchUserFiles = useCallback(
    async (userId) => {
      if (!userId) return
      try {
        const mtcRef = storageRef(storage, `mtc/${userId}`)
        const mtcList = await listAll(mtcRef)
        const mtcUrls = await Promise.all(
          mtcList.items.map(async (file) => ({
            name: file.name,
            url: await getDownloadURL(file),
          })),
        )

        const statementRef = storageRef(storage, `user-statements/${userId}`)
        const statementList = await listAll(statementRef)
        const statementUrls = await Promise.all(
          statementList.items.map(async (file) => ({
            name: file.name,
            url: await getDownloadURL(file),
          })),
        )

        setMtcFiles(mtcUrls.length > 0 ? mtcUrls.reverse() : [])
        setStatementFiles(statementUrls.length > 0 ? statementUrls.reverse() : [])
      } catch (err) {
        console.error(`Error fetching files: ${err.message}`)
        setMtcFiles([])
        setStatementFiles([])
      }
    },
    [storage],
  )

  // Fetch refunds
  const fetchRefunds = useCallback(
    async (userId) => {
      if (!userId) return
      try {
        const refundsRef = ref(db, `refunds/${userId}`)
        const snapshot = await get(refundsRef)
        if (snapshot.exists()) {
          const refundsData = snapshot.val()
          const filteredRefunds = []
          Object.entries(refundsData).forEach(([refundId, refund]) => {
            if (refund.timestamp) {
              filteredRefunds.push({
                id: refundId,
                timestamp: refund.timestamp,
                balance: refund.balance || 0,
                brand: refund.brand || "N/A",
                codeId: refund.codeId || "N/A",
                status: refund.status || "Pending",
              })
            } else {
              Object.entries(refund).forEach(([timestamp, details]) => {
                filteredRefunds.push({
                  id: refundId,
                  timestamp,
                  balance: details.balance || 0,
                  brand: details.brand || "N/A",
                  codeId: details.codeId || "N/A",
                  status: details.status || "Pending",
                })
              })
            }
          })
          setRefunds(filteredRefunds)
        } else {
          setRefunds([])
        }
      } catch (error) {
        console.error("Error fetching refunds:", error)
        setRefunds([])
      }
    },
    [db],
  )

  // Fetch user codes
  const fetchUserCodes = useCallback(
    async (userId) => {
      if (!userId) return
      try {
        const userCodesRef = ref(db, `new-code/${userId}/codes`)
        const codesSnapshot = await get(userCodesRef)
        if (codesSnapshot.exists()) {
          const codesData = codesSnapshot.val()
          const codesArray = Object.entries(codesData).map(([codeKey, codeData]) => {
            const sitesArray = codeData.sites
              ? Object.entries(codeData.sites).map(([siteKey, siteData]) => ({
                  siteNumber: siteKey,
                  address: siteData.address || "",
                  state: siteData.state || "",
                  pincode: siteData.pincode || "",
                }))
              : []
            return {
              id: codeKey,
              availableBalance: codeData.balance || 0,
              brand: codeData.brand || "N/A",
              codeId: codeData.codeID || codeKey,
              code: codeData.code || codeKey,
              timestamp: codeData.timestamp || codeData.createdAt || Date.now(),
              status: codeData.status || "active",
              sites: sitesArray,
              ...codeData,
            }
          })
          setUserCodes(codesArray)
        } else {
          setUserCodes([])
        }
      } catch (err) {
        console.error("Error fetching user codes:", err)
        setUserCodes([])
      }
    },
    [db],
  )


// ==========================
// Fetch Order History (Overview)
// ==========================
const fetchOrderHistory = useCallback(
    async (userId) => {
      if (!userId) return
      try {
        const ordersRef = ref(db, `orders/${userId}`)
        const userOrdersRef = ref(db, `userOrders/${userId}`)

        const [ordersSnapshot, userOrdersSnapshot] = await Promise.all([get(ordersRef), get(userOrdersRef)])

        let ordersArray = []

        if (ordersSnapshot.exists()) {
          const ordersData = ordersSnapshot.val()
          const orders = Object.entries(ordersData).map(([orderId, orderData]) => ({
            id: orderId,
            code: orderData.code || "N/A",
            brand: orderData.brand || "N/A",
            existingBalance: orderData.existingBalance || 0,
            grade: orderData.grade || "N/A",
            paymentAmount: orderData.paymentAmount || 0,
            quantity: orderData.quantity || 0,
            quantityType: orderData.quantityType || "MT",
            siteAddress: orderData.siteAddress || {},
            status: orderData.status || "pending",
            timestamp: orderData.timestamp || Date.now(),
            dispatched: orderData.dispatched || 0,
          }))
          ordersArray = [...ordersArray, ...orders]
        }

        if (userOrdersSnapshot.exists()) {
          const userOrdersData = userOrdersSnapshot.val()
          const userOrders = Object.entries(userOrdersData).map(([orderId, orderData]) => ({
            id: orderId,
            code: orderData.code || "N/A",
            brand: orderData.brand || "N/A",
            existingBalance: orderData.existingBalance || 0,
            grade: orderData.grade || "N/A",
            paymentAmount: orderData.paymentAmount || 0,
            quantity: orderData.quantity || 0,
            quantityType: orderData.quantityType || "MT",
            siteAddress: orderData.siteAddress || {},
            status: orderData.status || "pending",
            timestamp: orderData.timestamp || Date.now(),
            dispatched: orderData.dispatched || 0,
          }))
          ordersArray = [...ordersArray, ...userOrders]
        }

        const uniqueOrders = ordersArray.filter(
          (order, index, self) => index === self.findIndex((o) => o.id === order.id),
        )

        setOrderHistory(uniqueOrders)

        const pendingCount = uniqueOrders.filter((order) => order.status.toLowerCase() === "pending").length
        setPendingOrdersCount(pendingCount)
      } catch (err) {
        console.error("Error fetching order history:", err)
        setOrderHistory([])
        setPendingOrdersCount(0)
      }
    },
    [db],
  )


// ==========================
// Fetch Active POs (Overview Only)
// ==========================
const fetchActivePOs = useCallback(async (userId) => {
  if (!userId) return;

  try {
    const activePORef = ref(db, `ActivePO/${userId}`);
    const snapshot = await get(activePORef);

    if (snapshot.exists()) {
      const data = snapshot.val();

     const poArray = Object.entries(data).map(([poId, poData]) => ({
  id: poId,
  poNumber: poData.PO || poId,
  brand: poData.brand || "N/A",
  grade: poData.grade || "N/A",
  code: poData.code || "N/A",
  quantity: poData.quantity || 0,
  quantityType: poData.quantityType || "MT",
  lastUpdated: poData.lastUpdated || Date.now(),
  status: poData.status || "pending",
  companyName: poData.companyName || "N/A",
  basicPrice: poData.basicPrice || "N/A",   // ✅ Add this line
}));


      setActivePOList(poArray);
    } else {
      setActivePOList([]);
    }
  } catch (err) {
    console.error("Error fetching ActivePOs:", err);
    setActivePOList([]);
  }
}, [db]);
const formatDate = (timestamp) => {
  if (!timestamp) return "N/A";
  const date = new Date(timestamp);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};



// ==========================
// Fetch Data when client changes
// ==========================
useEffect(() => {
  if (selectedClientId) {
    fetchActivePOs(selectedClientId);
  } else {
    setActivePOList([]);
  }
}, [selectedClientId, fetchActivePOs]);



  // Fetch pending items
  useEffect(() => {
    if (!selectedClientId) {
      setPendingItems([])
      return
    }

    const pendingMap = new Map()
    const addOrMerge = (key, item) => {
      if (pendingMap.has(key)) {
        const existing = pendingMap.get(key)
        existing.desc += `\n• ${item.desc}`
      } else {
        pendingMap.set(key, {
          ...item,
          desc: `• ${item.desc}`,
        })
      }
    }

    const unsubs = []
    const companyName = clients.find((c) => c.id === selectedClientId)?.name || "Unnamed"

    // 1. Incomplete Finance Forms
    const financeRef = ref(db, `Finance/${selectedClientId}`)
    const financeUnsub = onValue(
      financeRef,
      (snap) => {
        if (snap.exists()) {
          const allMissing = []
          Object.entries(snap.val()).forEach(([appId, app]) => {
            const docs = app.documents || {}
            const required = ["aadhar", "bank", "employment", "pan", "salary"]
            const missing = required.filter((f) => docs[f] !== "yes")
            if (missing.length) {
              allMissing.push(`Application ${appId}: missing ${missing.join(", ")}`)
            }
          })
          if (allMissing.length) {
            addOrMerge("finance", {
              type: "Incomplete Finance Form",
              desc: allMissing.join("\n• "),
            })
          }
        }
        setPendingItems(Array.from(pendingMap.values()))
      },
      (err) => console.error("Error fetching finance:", err),
    )
    unsubs.push(financeUnsub)

    // 2. Missing Statement
    if (statementFiles.length === 0) {
      addOrMerge("statement", {
        type: "Missing Statement",
        desc: `Statement not uploaded for ${companyName}`,
      })
      setPendingItems(Array.from(pendingMap.values()))
    }

    // 3. Missing MTC
    if (mtcFiles.length === 0) {
      addOrMerge("mtc", {
        type: "Missing MTC Upload",
        desc: `MTC files not uploaded for ${companyName}`,
      })
      setPendingItems(Array.from(pendingMap.values()))
    }

    // 4. Missing Documents
    const requiredDocs = ["cancelCheque", "gstCopy", "panCard", "partnershipDeed", "purchaseOrder", "workOrder"]
    const missingDocs = requiredDocs.filter((d) => !userDocuments?.[d])
    if (missingDocs.length > 0) {
      addOrMerge("docs", {
        type: "Missing Documents",
        desc: `Missing: ${missingDocs.join(", ")}`,
      })
      setPendingItems(Array.from(pendingMap.values()))
    }

    // 5. Incomplete Order Site Info
    const ordersRef = ref(db, `userOrders/${selectedClientId}`)
    const ordersUnsub = onValue(
      ordersRef,
      (snap) => {
        if (snap.exists()) {
          Object.entries(snap.val()).forEach(([orderId, order]) => {
            const site = order.siteAddress || {}
            const missing = [!site.address && "address", !site.pincode && "pincode", !site.state && "state"].filter(
              Boolean,
            )
            if (missing.length > 0) {
              addOrMerge(`order-site-${orderId}`, {
                type: "Incomplete Site Info",
                desc: `Order ${orderId}: missing ${missing.join(", ")}`,
              })
            }
          })
          setPendingItems(Array.from(pendingMap.values()))
        }
      },
      (err) => console.error("Error fetching orders:", err),
    )
    unsubs.push(ordersUnsub)

    // 6. New Code Sites
    const codeRef = ref(db, `new-code/${selectedClientId}/codes`)
    const codeUnsub = onValue(
      codeRef,
      (snap) => {
        if (snap.exists()) {
          Object.entries(snap.val()).forEach(([codeKey, codeObj]) => {
            const codeValue = codeObj.codeID || codeKey
            if (codeObj.sites && Array.isArray(codeObj.sites)) {
              codeObj.sites.forEach((site, idx) => {
                const missingFields = [
                  !site.address && "address",
                  !site.pincode && "pincode",
                  !site.state && "state",
                ].filter(Boolean)
                if (missingFields.length > 0) {
                  addOrMerge(`new-code-${codeKey}-${idx}`, {
                    type: "Incomplete Site Info",
                    desc: `CodeID: ${codeValue} -> missing ${missingFields.join(", ")}`,
                  })
                }
              })
            }
          })
          setPendingItems(Array.from(pendingMap.values()))
        }
      },
      (err) => console.error("Error fetching codes:", err),
    )
    unsubs.push(codeUnsub)

    return () => unsubs.forEach((unsub) => unsub())
  }, [selectedClientId, userDocuments, statementFiles, mtcFiles, db, clients])

  // Fetch client data
  const fetchClientData = useCallback(
    (uid) => {
      if (!uid) return () => {}
      const sections = [
        "profileDashboard",
        "refunds",
        "userOrders",
        "queries",
        "quoteRequests",
        "rateRequest",
        "new-code",
        "userDocuments",
        "Finance",
        "mtc",
        "user-statements",
      ]
      const result = {}
      const unsubs = sections.map((section) =>
        onValue(
          ref(db, `${section}/${uid}`),
          (snapshot) => {
            result[section] = snapshot.exists() ? snapshot.val() : null
            setClientData({ ...result })
          },
          (err) => console.error(`Error fetching ${section}:`, err),
        ),
      )

      fetchUserDocuments(uid)
      fetchUserFiles(uid)
      fetchUserCodes(uid)
      fetchOrderHistory(uid)
      fetchRefunds(uid)

      return () => unsubs.forEach((unsub) => unsub())
    },
    [fetchUserDocuments, fetchUserFiles, fetchUserCodes, fetchOrderHistory, fetchRefunds, db],
  )

  useEffect(() => {
    if (selectedClientId) {
      const unsub = fetchClientData(selectedClientId)
      return unsub
    } else {
      setClientData({})
      setPendingItems([])
      setUserDocuments({})
      setMtcFiles([])
      setStatementFiles([])
      setUserCodes([])
      setOrderHistory([])
      setPendingOrdersCount(0)
      setRefunds([])
    }
  }, [selectedClientId, fetchClientData])

  const handleClientSelect = (e) => {
    setSelectedClientId(e.target.value)
  }

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: "cid-status-badge-pending",
      refund: "cid-status-badge-refund",
      clear: "cid-status-badge-clear",
      active: "cid-status-badge-active",
      processing: "cid-status-badge-processing",
      resolved: "cid-status-badge-resolved",
      "in-progress": "cid-status-badge-in-progress",
      requested: "cid-status-badge-pending",
      approved: "cid-status-badge-active",
      open: "cid-status-badge-pending",
      dispatched: "cid-status-badge-complete",
    }
    return statusMap[status?.toLowerCase()] || "cid-status-badge-default"
  }

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A"
    try {
      let date
      if (typeof timestamp === "string") {
        date = new Date(timestamp)
      } else if (timestamp.toDate) {
        date = timestamp.toDate()
      } else {
        date = new Date(Number.parseInt(timestamp, 10))
      }
      if (isNaN(date.getTime())) return "N/A"
      return format(date, "yyyy-MM-dd HH:mm")
    } catch {
      return "N/A"
    }
  }

  const getTimePeriodDays = (period) => {
    switch (period) {
      case "Last 1 Month":
        return 30
      case "Last 3 Months":
        return 90
      case "Last 6 Months":
        return 180
      case "Last 1 Year":
        return 365
      default:
        return 30
    }
  }

  const filterByTimePeriod = useMemo(
    () => (items, period) => {
      if (!items || !Array.isArray(items)) return []
      const days = getTimePeriodDays(period)
      const cutoffDate = Date.now() - days * 24 * 60 * 60 * 1000
      return items.filter((item) => {
        const timestamp = item.timestamp || item.submittedAt || Date.now()
        if (!timestamp) return false
        const itemDate = timestamp.toDate
          ? timestamp.toDate().getTime()
          : new Date(typeof timestamp === "string" ? timestamp : Number.parseInt(timestamp, 10)).getTime()
        return itemDate >= cutoffDate
      })
    },
    [],
  )

const getSiteWiseLifting = useMemo(
  () => () => {
    if (!orderHistory || !Array.isArray(orderHistory)) return {}
    const siteData = {}

    const filteredOrders = filterByTimePeriod(orderHistory, timePeriod)

    if (filteredOrders.length === 0) {
      return {}
    }

    filteredOrders.forEach((order) => {
      let siteName = "Unknown Site"
      if (order.siteAddress) {
        if (typeof order.siteAddress === "string") {
          siteName = order.siteAddress
        } else if (order.siteAddress.address) {
          siteName = order.siteAddress.address
        } else if (order.siteAddress.siteName) {
          siteName = order.siteAddress.siteName
        }
      }

      const status = order.status?.toLowerCase() || "pending"
      const quantity = Number.parseFloat(order.quantity || 0)

      // 👇 Fallback: if no dispatched, assume full quantity
      const dispatched = Number.parseFloat(
        order.dispatched !== undefined ? order.dispatched : quantity
      )

      if (!siteData[siteName]) {
        siteData[siteName] = {
          totalOrdered: 0,
          totalDispatched: 0,
          status: status === "dispatched" ? "Material Lifted" : "Pending",
          orders: [],
        }
      }

      siteData[siteName].totalOrdered += quantity
      siteData[siteName].totalDispatched += dispatched
      siteData[siteName].orders.push({
        id: order.id,
        brand: order.brand || "N/A",
        quantity: quantity,
        dispatched: dispatched,
        quantityType: order.quantityType || "MT",
        status: order.status || "Pending",
        timestamp: order.timestamp,
      })

      const allDispatched = siteData[siteName].orders.every(
        (o) => o.status?.toLowerCase() === "dispatched" || o.dispatched >= o.quantity,
      )
      siteData[siteName].status = allDispatched ? "Material Lifted" : "Pending"
    })

    return siteData
  },
  [orderHistory, timePeriod, filterByTimePeriod],
)


  const getStatusLabel = (explicitStatus, quotes) => {
    if (explicitStatus) return capitalize(explicitStatus)
    if (quotes.some((q) => q.negotiations)) return "Negotiated"
    if (quotes.some((q) => q.supplierReply)) return "Supplier-Replied"
    return "Pending"
  }

  const getStatusBgColor = (explicitStatus, quotes) => {
    if (explicitStatus === "completed") return "#22c55e"
    if (quotes.some((q) => q.negotiations)) return "#3b82f6"
    if (quotes.some((q) => q.supplierReply)) return "#eab308"
    return "#ed2d0bff"
  }

  const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : "")

  // Calculate order statistics
  const getOrderStatistics = useMemo(() => {
    if (!orderHistory || !Array.isArray(orderHistory)) {
      return {
        pending: 0,
        dispatched: 0,
        underloading: 0,
      }
    }
    const stats = {
      pending: 0,
      dispatched: 0,
      underloading: 0,
    }

    orderHistory.forEach((order) => {
      const status = order.status?.toLowerCase() || "pending"
      const quantity = Number.parseFloat(order.quantity || 0)
      const dispatched = Number.parseFloat(order.dispatched || 0)

      if (status === "pending") {
        stats.pending++
      } else if (status === "dispatched" || dispatched >= quantity) {
        stats.dispatched++
      } else if (dispatched > 0 && dispatched < quantity) {
        stats.underloading++
      }
    })

    return stats
  }, [orderHistory])

  const navigateToPage = (pageName) => {
    // This would typically use React Router or Next.js router
    // For now, we'll use window.location or you can replace with your routing solution
    window.location.href = `/${pageName}`
  }

const SectionHeader = ({ title, isOpen, onToggle, count, navigateTo }) => {
  const handleNavigate = (e) => {
    e.stopPropagation()

    if (navigateTo) {
      const event = new CustomEvent("changeView", {
        detail: {
          view: navigateTo,
          source: "sectionHeader",
        },
      })
      window.dispatchEvent(event)
    }
  }

  return (
    <div className="cid-section-header">
      <div className="cid-section-header-content" onClick={onToggle}>
        <div className="cid-section-title">
          <h2>{title}</h2>
          {count !== undefined && <span className="cid-section-count">{count}</span>}
        </div>
        <div className="cid-toggle-icon">
          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </div>
      {navigateTo && (
        <button
          className="cid-navigate-btn"
          onClick={handleNavigate}
          title={`Navigate to ${navigateTo}`}
        >
          <ExternalLink size={16} />
        </button>
      )}
    </div>
  )
}

  return (
    <div className="cid-container">
      <div className="cid-header">
        <h1>Client Data Management</h1>
        <p>Comprehensive client overview and management system</p>
      </div>

      {/* Section 1: Client Personal Details */}
      <div className="cid-section">
        <SectionHeader
          title="1. Client Personal Details"
          isOpen={toggleStates.personalDetails}
          onToggle={() => toggleSection("personalDetails")}
        />
        {toggleStates.personalDetails && (
          <div className="cid-section-content">
            <div className="cid-client-selector">
              <label>Select Client</label>
              <select onChange={handleClientSelect} value={selectedClientId}>
                <option value="">Choose a client...</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>
            {selectedClientId && clientData.profileDashboard && (
              <div className="cid-client-details">
                <div className="cid-detail-item">
                  <strong>Company:</strong> {clientData.profileDashboard.company_name || "N/A"}
                </div>
                <div className="cid-detail-item">
                  <strong>Email:</strong> {clientData.profileDashboard.email || "N/A"}
                </div>
                <div className="cid-detail-item">
                  <strong>Phone:</strong> {clientData.profileDashboard.phone || "N/A"}
                </div>
                <div className="cid-detail-item">
                  <strong>GSTIN:</strong> {clientData.profileDashboard.gstin || "N/A"}
                </div>
                <div className="cid-detail-item">
                  <strong>PAN:</strong> {clientData.profileDashboard.pan || "N/A"}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {selectedClientId && (
        <>
          {/* Section 2: Site-Wise Balances */}
          <div className="cid-section">
<SectionHeader
  title="2. Site-Wise Balances"
  isOpen={toggleStates.siteBalances}
  onToggle={() => toggleSection("siteBalances")}
  count={userCodes.length}
  navigateTo="userOrders"   
/>
            {toggleStates.siteBalances && (
              <div className="cid-section-content">
                <div className="cid-balance-cards cid-scrollable-content">
                  {userCodes.slice().map((code, i) => (
                    <div key={i} className="cid-balance-card">
                      <div className="cid-site-name">
                        {code.brand || "Unknown Brand"}
                        {code.codeId && <div>CodeID: {code.codeId}</div>}
                        {code.sites && code.sites.length > 0 && (
                          <div className="cid-site-addresses">
                            {code.sites.slice().map((site, idx) => (
                              <div key={idx} className="cid-site-address">
                                Site {idx + 0}: {site.address || "N/A"}, {site.state || "N/A"}
                                {site.pincode ? ` - ${site.pincode}` : ""}
                              </div>
                            ))}
                            {/* {code.sites.length > 2 && (
                              <div className="cid-more-sites">+{code.sites.length - 2} more sites</div>
                            )} */}
                          </div>
                        )}
                      </div>
                      <div className="cid-balance-info">
                        <div>
                          Available Balance: <span className="highlight-balance">₹{code.availableBalance || 0}</span>
                        </div>
                        <span className={`cid-status-badge ${getStatusBadge(code.status)}`}>
                          {code.status || "Active"}
                        </span>
                      </div>
                    </div>
                  ))}
                  {userCodes.length === 0 && <p>No balances available.</p>}
                </div>
              </div>
            )}
          </div>

          {/* Section 3: Refund Requests */}
          <div className="cid-section">
            <SectionHeader
              title="3. Refund Requests"
              isOpen={toggleStates.refundRequests}
              onToggle={() => toggleSection("refundRequests")}
              count={refunds.length}
              navigateTo="refunds"

            />
            {toggleStates.refundRequests && (
              <div className="cid-section-content">
                <div className="cid-refund-cards cid-scrollable-content">
                  {refunds.slice().map((refund, index) => (
                    <div key={refund.id + refund.timestamp} className="cid-refund-card">
                      <h3>Refund Request #{index + 1}</h3>
                      <p>
                        <strong>Refund Amount:</strong> ₹{refund.balance}
                      </p>
                      <p>
                        <strong>Brand:</strong> {refund.brand}
                      </p>
                      <p>
                        <strong>Code ID:</strong> {refund.codeId}
                      </p>
                      <p>
                        <strong>Date:</strong> {formatTimestamp(refund.timestamp)}
                      </p>
                      <span className={`cid-status-badge ${getStatusBadge(refund.status)}`}>
                        {refund.status || "Pending"}
                      </span>
                    </div>
                  ))}
                  {refunds.length === 0 && <p className="cid-no-refunds">No refund requests available.</p>}
                </div>
              </div>
            )}
          </div>

          {/* Section 4: Query History */}
          <div className="cid-section">
            <SectionHeader
              title="4. Query History"
              isOpen={toggleStates.queryHistory}
              onToggle={() => toggleSection("queryHistory")}
              count={clientData.queries ? Object.keys(clientData.queries).length : 0}
              navigateTo="userQueries"
            />
            {toggleStates.queryHistory && (
              <div className="cid-section-content">
                <div className="cid-query-list cid-scrollable-content">
                  {clientData.queries &&
                    Object.values(clientData.queries)
                      .slice()
                      .map((query, i) => (
                        <div key={i} className="cid-query-item">
                          <div className="cid-query-title">
                            Query: {query.query?.substring(0, 50) || "Query"}
                            {query.query?.length > 50 && "..."}
                          </div>
                          <div className="cid-query-meta">Raised: {formatTimestamp(query.submittedAt)}</div>
                          <span
                            className={`cid-status-badge ${
                              query.supplierReply ? "cid-status-badge-resolved" : "cid-status-badge-in-progress"
                            }`}
                          >
                            {query.supplierReply ? "Resolved" : "In Progress"}
                          </span>
                        </div>
                      ))}
                  {!clientData.queries && <p className="cid-no-queries">No queries available.</p>}
                </div>
              </div>
            )}
          </div>

          {/* Section 5: Quote Rate History */}
          <div className="cid-section">
            <SectionHeader
              title="5. Quote Rate History (Site-wise)"
              isOpen={toggleStates.quoteHistory}
              onToggle={() => toggleSection("quoteHistory")}
              count={clientData.quoteRequests ? Object.keys(clientData.quoteRequests).length : 0}
              navigateTo="userQuotations"
            />
            {toggleStates.quoteHistory && (
              <div className="cid-section-content">
                <div className="cid-rate-history-grid cid-scrollable-content">
                  {clientData.quoteRequests &&
                    Object.entries(
                      Object.values(clientData.quoteRequests).reduce((acc, quote) => {
                        const site = quote.siteAddress || "Unknown Site"
                        if (!acc[site]) acc[site] = []
                        acc[site].push(quote)
                        return acc
                      }, {}),
                    )
                      .slice()
                      .map(([site, quotes]) => (
                        <div key={site} className="cid-rate-card">
                          <h4>
                            Site: {site.length > 30 ? site.substring(0, 30) + "..." : site}
                            <span
                              style={{
                                marginLeft: "10px",
                                padding: "2px 8px",
                                backgroundColor: getStatusBgColor(quotes.find((q) => q.status)?.status, quotes),
                                color: "white",
                                borderRadius: "12px",
                                fontSize: "12px",
                                fontWeight: "600",
                                display: "inline-block",
                              }}
                            >
                              {getStatusLabel(quotes.find((q) => q.status)?.status, quotes)}
                            </span>
                          </h4>
                          {quotes.slice().map((quote, i) => (
                            <div key={i} className="cid-rate-item">
                              <p>
                                <strong>Brand:</strong> {quote.brand || "N/A"}
                              </p>
                              <p>
                                <strong>Category:</strong> {quote.category || "N/A"}
                              </p>
                              <p>
                                <strong>Grade:</strong> {quote.grade || "N/A"}
                              </p>
                              <p>
                                <strong>Quantity:</strong> {quote.quantity} {quote.quantityUnit}
                              </p>
                            </div>
                          ))}
                          {quotes.length > 2 && <div className="cid-more-quotes">+{quotes.length - 2} more quotes</div>}
                        </div>
                      ))}
                  {!clientData.quoteRequests && <p className="cid-no-quotes">No quote requests available.</p>}
                </div>
              </div>
            )}
          </div>

          {/* Section 6: Current & Pending Purchase Orders */}
<div className="cid-section">
  <SectionHeader
    title="6. Current & Pending Purchase Orders"
    isOpen={toggleStates.purchaseOrders}
    onToggle={() => toggleSection("purchaseOrders")}
    count={activePOList.length}
    navigateTo="userOrders"
  />

  {toggleStates.purchaseOrders && (
    <div className="cid-section-content">
      <div className="cid-po-list cid-scrollable-content">
        {activePOList && activePOList.length > 0 ? (
          activePOList.map((po) => {
            const quantity = Number.parseFloat(po.quantity || 0)
            const quantityType = po.quantityType || "MT"
            const lastUpdated = formatDate(po.lastUpdated)

            // Map status to badge class and text
            const statusMap = {
              active: { class: "cid-status-badge-active", text: "Active" },
              pending: { class: "cid-status-badge-pending", text: "Pending" },
              expired: { class: "cid-status-badge-expired", text: "Expired" },
              complete: { class: "cid-status-badge-complete", text: "Complete" },
            }
            const { class: badgeClass, text: badgeText } = statusMap[po.status?.toLowerCase()] || {
              class: "cid-status-badge-default",
              text: po.status || "N/A",
            }

            return (
              <div key={po.id} className="cid-po-item-compact">
                <div className="cid-po-header-compact">
                  <div className="cid-po-main-info">
                    <strong>
                      {po.brand || "N/A"} - {po.grade || "N/A"}
                    </strong>
                    <span className="cid-po-quantity">
                      {quantity.toFixed(2)} {quantityType}
                    </span>
                  </div>
                  {/* Status badge */}
                  <span className={`cid-status-badge ${badgeClass}`}>{badgeText}</span>
                </div>

                <div className="cid-po-details-compact">
                   <p><strong>CodeID:</strong> {po.code || "N/A"}</p>
                  <p><strong>PO No:</strong> {po.poNumber || po.id}</p>
                  <p><strong>Company:</strong> {po.companyName || "N/A"}</p>
                  <p><strong>Basic Price:</strong> ₹{po.basicPrice || "N/A"}</p>
                  <p><strong>Last Updated:</strong> {lastUpdated}</p>
                </div>
              </div>
            )
          })
        ) : (
          <p className="cid-no-orders">No current or pending orders available.</p>
        )}
      </div>
    </div>
  )}
</div>




          {/* Section 7: Pending Actions */}
          <div className="cid-section">
            <SectionHeader
              title="7. Pending Actions"
              isOpen={toggleStates.pendingActions}
              onToggle={() => toggleSection("pendingActions")}
              count={pendingItems.length}
            />
            {toggleStates.pendingActions && (
              <div className="cid-section-content">
                <div className="pending-section cid-scrollable-content">
                  {pendingItems.length > 0 ? (
                    pendingItems.slice().map((item, i) => (
                      <div key={i} className="pending-item">
                        <div className="pending-title">{item.type}</div>
                        <div className="pending-desc" style={{ whiteSpace: "pre-line" }}>
                          {item.desc?.length > 100 ? item.desc.substring(0, 100) + "..." : item.desc}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="cid-no-pending">No pending actions.</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Section 8: New Codes */}
          <div className="cid-section">
            <SectionHeader
              title="8. New Open Codes"
              isOpen={toggleStates.newCodes}
              onToggle={() => toggleSection("newCodes")}
              count={userCodes.length}
            />
            {toggleStates.newCodes && (
              <div className="cid-section-content">
                <div className="cid-code-summary">
                  <p>
                    <strong>Total Codes:</strong> {userCodes.length}
                  </p>
                  <p>
                    <strong>Active Codes:</strong>{" "}
                    {userCodes.filter((code) => code.status?.toLowerCase() === "active").length}
                  </p>
                </div>
                <div className="cid-code-list cid-scrollable-content">
                  {userCodes.length > 0 ? (
                    userCodes.slice().map((code, i) => (
                      <div key={i} className="cid-code-item">
                        <div className="cid-code-title">
                          CodeID: {code.codeID || code.codeId || `NC-2024-00${i + 1}`}
                        </div>
                        <div className="cid-code-date">Generated: {formatTimestamp(code.timestamp)}</div>
                        <div className="cid-code-details">
                          <p>
                            <strong>Brand:</strong> {code.brand}
                          </p>
                        </div>
                        <span className={`cid-status-badge ${getStatusBadge(code.status)}`}>
                          {capitalize(code.status) || "Active"}
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="cid-no-codes">No codes available.</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Section 9: Client Lifting Summary */}
          <div className="cid-section">
            <SectionHeader
              title="9. Client Lifting Summary (Site-wise)"
              isOpen={toggleStates.liftingSummary}
              onToggle={() => toggleSection("liftingSummary")}
              navigateTo="userOrders"
            />
            {toggleStates.liftingSummary && (
              <div className="cid-section-content">
                <div className="cid-time-period-selector">
                  <label>Select Time Period</label>
                  <select value={timePeriod} onChange={(e) => setTimePeriod(e.target.value)}>
                    <option value="Last 1 Month">Last 1 Month</option>
                    <option value="Last 3 Months">Last 3 Months</option>
                    <option value="Last 6 Months">Last 6 Months</option>
                    <option value="Last 1 Year">Last 1 Year</option>
                  </select>
                </div>
                <div className="cid-lifting-summary-grid cid-scrollable-content">
                  {Object.entries(getSiteWiseLifting()).length > 0 ? (
                    Object.entries(getSiteWiseLifting())
                      .slice()
                      .map(([site, data]) => (
                        <div key={site} className="cid-lifting-card-compact">
                          <div className="cid-site-name-compact">
                            {site.length > 25 ? site.substring(0, 25) + "..." : site}
                          </div>
                          <div className="cid-lifting-summary-stats">
                            <div className="cid-lifting-stat">
                              <span className="cid-stat-label">Ordered:</span>
                              <span className="cid-stat-value">
                                {data.totalOrdered.toFixed(2)} {data.orders[0]?.quantityType || "MT"}
                              </span>
                            </div>
                            <div className="cid-lifting-stat">
                              <span className="cid-stat-label">Dispatched:</span>
                              <span className="cid-stat-value">
                                {data.totalDispatched.toFixed(2)} {data.orders[0]?.quantityType || "MT"}
                              </span>
                            </div>
                            <div className="cid-lifting-stat">
                              <span className="cid-stat-label">Orders:</span>
                              <span className="cid-stat-value">{data.orders.length}</span>
                            </div>
                          </div>
                          <div className="cid-lifting-status-compact">{data.status}</div>
                        </div>
                      ))
                  ) : (
                    <div className="cid-lifting-card cid-lifting-card-no-activity">
                      <div className="cid-site-name">No Data Available</div>
                      <div className="cid-lifting-amount">0.00 MT</div>
                      <div className="cid-lifting-status">No activity in selected period</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default ClientInfoDashboard
